import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Fullscreen image viewer — pinch / double-tap zoom, pan, swipe between images.
 * index.html ปิด browser pinch (user-scalable=no) จึงต้องจัดการ gesture เอง
 */
@Component({
  selector: 'app-image-viewer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './image-viewer.component.html',
  styleUrl: './image-viewer.component.css',
})
export class ImageViewerComponent implements OnInit {
  @Input() images: string[] = [];
  @Input() startIndex = 0;
  @Input() lang: 'TH' | 'EN' = 'TH';
  @Output() closed = new EventEmitter<void>();

  @ViewChild('stage', { static: true }) stageRef!: ElementRef<HTMLDivElement>;
  @ViewChild('img') imgRef?: ElementRef<HTMLImageElement>;

  index = 0;
  scale = 1;
  tx = 0;
  ty = 0;
  animating = false;

  private readonly maxScale = 4;
  private readonly doubleTapScale = 2.5;
  private readonly swipeThreshold = 60;

  private pinchStartDist = 0;
  private pinchStartScale = 1;
  private pinchStartMid = { x: 0, y: 0 };
  private pinchStartT = { x: 0, y: 0 };
  private panStart = { x: 0, y: 0 };
  private panStartT = { x: 0, y: 0 };
  private isPinching = false;
  private lastTapAt = 0;
  private lastTapPoint = { x: 0, y: 0 };

  /*#################################################### Function ################################################*/

  ngOnInit(): void {
    const max = Math.max(0, this.images.length - 1);
    this.index = Math.min(Math.max(0, this.startIndex), max);
  }

  get current(): string {
    return this.images[this.index] || '';
  }

  get hasMany(): boolean {
    return this.images.length > 1;
  }

  get transform(): string {
    return `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
  }

  close(): void {
    this.closed.emit();
  }

  prev(): void {
    if (this.index <= 0) return;
    this.index--;
    this.resetTransform(false);
  }

  next(): void {
    if (this.index >= this.images.length - 1) return;
    this.index++;
    this.resetTransform(false);
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === this.stageRef.nativeElement && this.scale === 1) {
      this.close();
    }
  }

  onTouchStart(event: TouchEvent): void {
    this.animating = false;
    if (event.touches.length === 2) {
      this.isPinching = true;
      this.pinchStartDist = this.distance(event.touches[0], event.touches[1]);
      this.pinchStartScale = this.scale;
      this.pinchStartMid = this.midpoint(event.touches[0], event.touches[1]);
      this.pinchStartT = { x: this.tx, y: this.ty };
      return;
    }
    if (event.touches.length === 1) {
      const t = event.touches[0];
      this.panStart = { x: t.clientX, y: t.clientY };
      this.panStartT = { x: this.tx, y: this.ty };
    }
  }

  onTouchMove(event: TouchEvent): void {
    event.preventDefault();

    if (event.touches.length === 2 && this.isPinching) {
      const dist = this.distance(event.touches[0], event.touches[1]);
      const mid = this.midpoint(event.touches[0], event.touches[1]);
      const nextScale = this.clamp(
        this.pinchStartScale * (dist / this.pinchStartDist),
        0.8,
        this.maxScale,
      );
      const c = this.stageCenter();
      const ratio = nextScale / this.pinchStartScale;
      this.tx = mid.x - c.x - (this.pinchStartMid.x - c.x - this.pinchStartT.x) * ratio;
      this.ty = mid.y - c.y - (this.pinchStartMid.y - c.y - this.pinchStartT.y) * ratio;
      this.scale = nextScale;
      return;
    }

    if (event.touches.length === 1 && !this.isPinching) {
      const t = event.touches[0];
      const dx = t.clientX - this.panStart.x;
      const dy = t.clientY - this.panStart.y;
      if (this.scale > 1) {
        this.tx = this.panStartT.x + dx;
        this.ty = this.panStartT.y + dy;
      } else if (this.hasMany) {
        this.tx = dx;
      }
    }
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.isPinching) {
      if (event.touches.length < 2) {
        this.isPinching = false;
        if (event.touches.length === 1) {
          const t = event.touches[0];
          this.panStart = { x: t.clientX, y: t.clientY };
          this.panStartT = { x: this.tx, y: this.ty };
        }
        if (this.scale < 1) {
          this.resetTransform(true);
        } else {
          this.clampTranslate(true);
        }
      }
      return;
    }

    if (event.touches.length > 0) return;

    // กัน ghost click / dblclick ซ้ำกับ gesture ที่จัดการเองแล้ว
    if (event.cancelable) event.preventDefault();

    const touch = event.changedTouches[0];
    const dx = touch.clientX - this.panStart.x;
    const dy = touch.clientY - this.panStart.y;
    const isTap = Math.abs(dx) < 10 && Math.abs(dy) < 10;

    if (isTap) {
      if (event.target === this.stageRef.nativeElement && this.scale === 1) {
        this.close();
        return;
      }
      this.handleTap(touch.clientX, touch.clientY);
      return;
    }

    if (this.scale > 1) {
      this.clampTranslate(true);
      return;
    }

    if (this.hasMany && Math.abs(dx) > this.swipeThreshold) {
      if (dx < 0 && this.index < this.images.length - 1) {
        this.next();
        return;
      }
      if (dx > 0 && this.index > 0) {
        this.prev();
        return;
      }
    }
    this.resetTransform(true);
  }

  onDoubleClick(event: MouseEvent): void {
    this.toggleZoomAt(event.clientX, event.clientY);
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const factor = event.deltaY < 0 ? 1.15 : 1 / 1.15;
    const nextScale = this.clamp(this.scale * factor, 1, this.maxScale);
    this.zoomTo(nextScale, event.clientX, event.clientY, false);
    if (nextScale === 1) this.resetTransform(false);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.close();
    if (event.key === 'ArrowLeft') this.prev();
    if (event.key === 'ArrowRight') this.next();
  }

  private handleTap(x: number, y: number): void {
    const now = Date.now();
    const near =
      Math.abs(x - this.lastTapPoint.x) < 30 && Math.abs(y - this.lastTapPoint.y) < 30;
    if (now - this.lastTapAt < 300 && near) {
      this.lastTapAt = 0;
      this.toggleZoomAt(x, y);
      return;
    }
    this.lastTapAt = now;
    this.lastTapPoint = { x, y };
  }

  private toggleZoomAt(x: number, y: number): void {
    if (this.scale > 1) {
      this.resetTransform(true);
      return;
    }
    this.zoomTo(this.doubleTapScale, x, y, true);
  }

  private zoomTo(nextScale: number, fx: number, fy: number, animate: boolean): void {
    const c = this.stageCenter();
    const ratio = nextScale / this.scale;
    this.animating = animate;
    this.tx = fx - c.x - (fx - c.x - this.tx) * ratio;
    this.ty = fy - c.y - (fy - c.y - this.ty) * ratio;
    this.scale = nextScale;
    this.clampTranslate(animate);
  }

  private clampTranslate(animate: boolean): void {
    const img = this.imgRef?.nativeElement;
    const stage = this.stageRef.nativeElement;
    if (!img) return;
    const maxX = Math.max(0, (img.offsetWidth * this.scale - stage.clientWidth) / 2);
    const maxY = Math.max(0, (img.offsetHeight * this.scale - stage.clientHeight) / 2);
    this.animating = animate;
    this.tx = this.clamp(this.tx, -maxX, maxX);
    this.ty = this.clamp(this.ty, -maxY, maxY);
  }

  private resetTransform(animate: boolean): void {
    this.animating = animate;
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
  }

  private stageCenter(): { x: number; y: number } {
    const rect = this.stageRef.nativeElement.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  private distance(a: Touch, b: Touch): number {
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }

  private midpoint(a: Touch, b: Touch): { x: number; y: number } {
    return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }
}
